import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, FileText, Shield, Clock, Truck, CreditCard, AlertCircle, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import './Terminos.css';

const Terminos = () => {

  const currentDate = new Date().toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Términos y Condiciones - Artelaser3D",
    "description": "Términos y condiciones de uso de los servicios de grabado láser e impresión 3D de Artelaser3D en Asturias. Información legal, condiciones de contratación, envíos y política de privacidad.",
    "url": "https://artelaser3d.es/terminos",
    "lastReviewed": new Date().toISOString().split('T')[0],
    "mainEntity": {
      "@type": "Organization",
      "name": "Artelaser3D",
      "url": "https://artelaser3d.es",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Asturias",
        "addressCountry": "ES"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Service",
        "availableLanguage": ["Spanish"]
      }
    }
  };

  const termsSections = [
    {
      id: 'uso-web',
      icon: <FileText size={24} />,
      title: '1. Uso del Sitio Web',
      content: [
        'El acceso y uso de este sitio web atribuye la condición de usuario, aceptando desde dicho acceso las presentes condiciones generales.',
        'Artelaser3D se reserva el derecho a modificar en cualquier momento las presentes condiciones, siendo vigentes las publicadas en el momento de la navegación.',
        'El usuario se compromete a hacer un uso adecuado del sitio web, absteniéndose de realizar actividades ilícitas, contrarias a la buena fe o al orden público.'
      ]
    },
    {
      id: 'condiciones-servicio',
      icon: <Scale size={24} />,
      title: '2. Condiciones de Contratación',
      content: [
        'Los servicios ofrecidos incluyen grabado láser, corte láser, impresión 3D, diseño personalizado y prototipado.',
        'Los presupuestos tienen una validez de 30 días desde su emisión, salvo indicación expresa en contrario.',
        'La realización del trabajo requerirá la aceptación del presupuesto y, en proyectos superiores a 150€, el pago de un anticipo del 50%.',
        'Los diseños y archivos proporcionados por el cliente deben ser de su propiedad o tener licencia de uso. Artelaser3D no se hace responsable de la violación de derechos de propiedad intelectual de terceros.',
        'Nos reservamos el derecho de rechazar proyectos que contengan contenido ofensivo, discriminatorio, ilegal o que viole derechos de terceros.'
      ]
    },
    {
      id: 'plazos-entrega',
      icon: <Clock size={24} />,
      title: '3. Plazos y Entrega',
      content: [
        'Los plazos de entrega indicados son estimados y comienzan a contar desde la confirmación del pago y la recepción de todos los materiales/archivos necesarios.',
        'Los plazos pueden variar según la complejidad del proyecto, disponibilidad de materiales y carga de trabajo actual.',
        'El cliente será informado de cualquier retraso significativo en la producción.',
        'Para proyectos urgentes (entrega en menos de 72 horas), se aplicará un suplemento del 25% sobre el precio total.',
        'El cliente debe revisar el producto en el momento de la entrega. No se aceptarán reclamaciones por defectos visibles transcurridas 48 horas desde la entrega.'
      ]
    },
    {
      id: 'envios-transporte',
      icon: <Truck size={24} />,
      title: '4. Envíos y Transporte',
      content: [
        'Realizamos envíos a toda España (Península, Baleares, Canarias) y, bajo consulta, a otros países de la UE.',
        'Los gastos de envío no están incluidos en el presupuesto salvo indicación expresa.',
        'El riesgo de pérdida o daño durante el transporte recae en el cliente una vez entregado el paquete al transportista.',
        'Los plazos de entrega del transportista son orientativos y dependen de la empresa de mensajería seleccionada.',
        'Para envíos internacionales, el cliente es responsable de los aranceles, impuestos y tasas aduaneras aplicables.'
      ]
    },
    {
      id: 'pagos',
      icon: <CreditCard size={24} />,
      title: '5. Precios y Formas de Pago',
      content: [
        'Todos los precios indicados incluyen el IVA (21%) salvo que se especifique lo contrario.',
        'Formas de pago aceptadas: transferencia bancaria, bizum, tarjeta de crédito/débito y PayPal.',
        'Para proyectos de importe superior a 150€, se requiere un anticipo del 50% para iniciar la producción.',
        'El importe restante debe abonarse antes de la entrega o envío del producto.',
        'No se entregarán productos ni se realizarán envíos hasta la recepción completa del pago.'
      ]
    },
    {
      id: 'garantias',
      icon: <Shield size={24} />,
      title: '6. Garantía y Devoluciones',
      content: [
        'Todos nuestros productos están garantizados contra defectos de fabricación durante un período de 3 meses desde la entrega.',
        'La garantía no cubre daños por uso inadecuado, manipulación incorrecta, accidentes o desgaste natural.',
        'Los productos personalizados, grabados o impresos según especificaciones del cliente no tienen derecho a devolución por desistimiento, salvo defectos de fabricación.',
        'En caso de producto defectuoso, el cliente debe notificarlo por escrito en un plazo máximo de 7 días desde la recepción.',
        'La solución adoptada para productos defectuosos puede ser la reparación, sustitución o reembolso, a elección de Artelaser3D.'
      ]
    },
    {
      id: 'propiedad-intelectual',
      icon: <AlertCircle size={24} />,
      title: '7. Propiedad Intelectual',
      content: [
        'Los diseños, logos, textos y demás contenidos de este sitio web son propiedad de Artelaser3D o de sus respectivos propietarios, estando protegidos por la legislación sobre propiedad intelectual.',
        'El cliente garantiza que los archivos, diseños o materiales proporcionados para la realización del trabajo son de su propiedad o que dispone de la autorización necesaria para su uso.',
        'Artelaser3D se reserva el derecho de utilizar imágenes de los trabajos realizados para fines promocionales, salvo que el cliente solicite expresamente lo contrario por escrito.',
        'Los diseños creados por Artelaser3D para proyectos específicos son propiedad de Artelaser3D, salvo acuerdo de cesión de derechos pactado por escrito.'
      ]
    },
    {
      id: 'proteccion-datos',
      icon: <Shield size={24} />,
      title: '8. Protección de Datos',
      content: [
        'Los datos personales proporcionados serán tratados conforme al Reglamento (UE) 2016/679 (GDPR) y la Ley Orgánica 3/2018 de Protección de Datos Personales.',
        'Responsable del tratamiento: Artelaser3D. Finalidad: gestionar pedidos, presupuestos y comunicaciones comerciales.',
        'Legitimación: ejecución de contrato y consentimiento del interesado.',
        'Conservación: los datos se conservarán mientras sean necesarios para la finalidad indicada y durante los plazos legalmente establecidos.',
        'Derechos: puede ejercer sus derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad dirigiéndose a info@artelaser3d.es.',
        'Más información en nuestra Política de Privacidad.'
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Términos y Condiciones"
        description="Términos y condiciones de uso de Artelaser3D. Información legal sobre contratación de servicios de grabado láser e impresión 3D, envíos, pagos y protección de datos en Asturias."
        keywords="términos y condiciones grabado láser Asturias, condiciones contratación impresión 3D, política privacidad Artelaser3D, condiciones legales servicios personalizados Asturias"
        canonicalUrl="https://artelaser3d.es/terminos"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="page-hero page-hero--legal">
        <div className="page-hero__container">
          <span className="page-hero__badge">Información Legal</span>
          <h1 className="page-hero__title">
            Términos y
            <span className="text-primary"> Condiciones</span>
          </h1>
          <p className="page-hero__description">
            Información legal sobre el uso de nuestros servicios de grabado láser e impresión 3D. 
            Condiciones de contratación, envíos, pagos y protección de datos.
          </p>
          <p className="page-hero__updated">
            Última actualización: {currentDate}
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section section--legal-intro section--visible">
        <div className="section__container">
          <div className="legal-intro-card">
            <div className="legal-intro-icon">
              <Scale size={40} />
            </div>
            <div className="legal-intro-content">
              <h2>Acuerdo Legal</h2>
              <p>
                El presente documento regula las condiciones generales de acceso y uso del sitio web 
                <strong> artelaser3d.es</strong>, así como las condiciones de contratación de los 
                servicios de grabado láser, corte láser e impresión 3D ofrecidos por Artelaser3D, 
                ubicados en Asturias y con servicio en toda España.
              </p>
              <p>
                Al acceder a este sitio web y/o contratar nuestros servicios, usted acepta quedar 
                vinculado por estos términos y condiciones. Le recomendamos leer detenidamente 
                este documento antes de realizar cualquier contratación.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Sections */}
      <section className="section section--terms section--visible">
        <div className="section__container">
          <div className="terms-grid">
            {termsSections.slice(0, 2).map((section) => (
              <article key={section.id} id={section.id} className="terms-card">
                <div className="terms-card__header">
                  <div className="terms-card__icon">{section.icon}</div>
                  <h2>{section.title}</h2>
                </div>
                <div className="terms-card__content">
                  {section.content.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--terms section--visible">
        <div className="section__container">
          <div className="terms-grid">
            {termsSections.slice(2, 4).map((section) => (
              <article key={section.id} id={section.id} className="terms-card">
                <div className="terms-card__header">
                  <div className="terms-card__icon">{section.icon}</div>
                  <h2>{section.title}</h2>
                </div>
                <div className="terms-card__content">
                  {section.content.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--terms section--visible">
        <div className="section__container">
          <div className="terms-grid">
            {termsSections.slice(4, 6).map((section) => (
              <article key={section.id} id={section.id} className="terms-card">
                <div className="terms-card__header">
                  <div className="terms-card__icon">{section.icon}</div>
                  <h2>{section.title}</h2>
                </div>
                <div className="terms-card__content">
                  {section.content.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--terms section--visible">
        <div className="section__container">
          <div className="terms-grid">
            {termsSections.slice(6, 8).map((section) => (
              <article key={section.id} id={section.id} className="terms-card">
                <div className="terms-card__header">
                  <div className="terms-card__icon">{section.icon}</div>
                  <h2>{section.title}</h2>
                </div>
                <div className="terms-card__content">
                  {section.content.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Legal Info */}
      <section 
        className="section section--legal-additional section--visible"
      >
        <div className="section__container">
          <div className="legal-additional-grid">
            <article className="legal-additional-card">
              <h3>Ley Aplicable y Jurisdicción</h3>
              <p>
                Las presentes condiciones se rigen por la legislación española. Para cualquier 
                controversia que pudiera derivarse del acceso al sitio web o de la contratación 
                de servicios, las partes se someten a los Juzgados y Tribunales de Oviedo, 
                Asturias, con renuncia expresa a cualquier otro fuero que pudiera corresponderles.
              </p>
            </article>

            <article className="legal-additional-card">
              <h3>Modificaciones</h3>
              <p>
                Artelaser3D se reserva el derecho de modificar estas condiciones en cualquier 
                momento, siendo vigentes las publicadas en el momento de la navegación. Se 
                recomienda revisar periódicamente este documento para estar informado de 
                posibles cambios.
              </p>
            </article>

            <article className="legal-additional-card">
              <h3>Contacto Legal</h3>
              <p>
                Para cualquier consulta relacionada con estos términos y condiciones, puede 
                contactarnos a través de:
              </p>
              <ul className="legal-contact-list">
                <li>
                  <Mail size={18} />
                  <span>Email: info@artelaser3d.es</span>
                </li>
              </ul>
            </article>

            <article className="legal-additional-card legal-additional-card--highlight">
              <h3>Consentimiento</h3>
              <p>
                Al utilizar este sitio web y contratar nuestros servicios, usted declara haber 
                leído, entendido y aceptado los presentes términos y condiciones en su totalidad.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section--cta section--cta--terminos section--visible">
        <div className="cta__content">
          <h2 className="cta__title--terminos">¿Tienes alguna duda?</h2>
          <p className="cta__description--terminos">
            Si necesitas aclaración sobre algún aspecto de nuestros términos y condiciones, 
            no dudes en contactarnos. Estaremos encantados de ayudarte.
          </p>
          <div className="cta__buttons">
            <Link to="/contacto" className="btn btn--primary btn--lg">
              Contactar
            </Link>
            <Link to="/faq" className="btn btn--outline btn--lg">
              Ver Preguntas Frecuentes
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Terminos;
